import { useState, useRef, useEffect } from 'react';
import { fetchBookings } from '../lib/api/bookings';
import {
  activityCommandsAreServerAuthoritative,
  applyActivityScopeEdit,
  cancelActivitiesCommand,
  createActivities,
  saveActivityWithOccurrences,
  upsertActivity,
} from '../lib/api/activities';
import { SaveRefused, saveWithRetry } from '../lib/saveWithRetry';

// ─────────────────────────────────────────────────────────────────────
// useActivityActions — the activity (signup/series) action handlers lifted
// VERBATIM out of Padel24AppMain (extracted from the former monolith) as a
// god-component collapse slice (PR-BE). The cluster is self-contained: its
// handlers call each other +
// the already-extracted hooks (persistActivities from useActivities,
// addManyBookings/deleteManyBookings from useBookingActions/useBookings) +
// nav/toast — never the tournament-lifecycle handlers. The hook is called in
// Padel24AppMain AFTER useBookingActions so addManyBookings is in scope (TDZ).
//
// State ownership: rollingToppedRef is private; seriesEditPending /
// seriesEditConfirm are owned here and RETURNED (the JSX shell renders the
// series-edit confirm modals from them). courtReservation / reservationDraft
// stay in the shell (shared with the serie-booking modal) and are passed in.
//
// R1/§13.20: all writes go through persistActivities / addManyBookings /
// deleteManyBookings (explicit Storage-first writers) — this hook never touches
// the loaded-flag refs; the blanket-persist effects in the slice hooks stay the
// sole flag-gated writers. The two effects (rolling top-up + auto-cancel) only
// read activities/bookings and re-run when those change, so they never
// write-before-load. defaultActivityDraft / topUpRollingSeries are internal.
// ─────────────────────────────────────────────────────────────────────
export interface UseActivityActionsDeps {
  activities: any;
  persistActivities: (list: any) => any;
  /** Adoption of what a command returned — never a write (#245). */
  adoptServerActivity: (saved: any) => void;
  adoptServerActivities: (saved: any[]) => void;
  refreshActivities: () => Promise<void>;
  bookings: any;
  bookingsRef: any;
  addManyBookings: (list: any, opts?: any) => any;
  deleteManyBookings: (ids: any) => any;
  /** Re-read the calendar after a command freed courts server-side (#245). */
  refreshBookings: () => Promise<void>;
  showToast: (kind: any, msg: any) => void;
  setView: (v: any) => void;
  setSetupFromActivities: (v: any) => void;
  setCustomActivityType: (v: any) => void;
  setEditingKMSetup: (v: any) => void;
  setPendingSetupLoad: (v: any) => void;
  setActivitiesOpenId: (v: any) => void;
  courtReservation: any;
  setCourtReservation: (v: any) => void;
  reservationDraft: any;
  setReservationDraft: (v: any) => void;
}

export function useActivityActions({
  activities,
  persistActivities,
  adoptServerActivity,
  adoptServerActivities,
  refreshActivities,
  bookings,
  bookingsRef,
  addManyBookings,
  deleteManyBookings,
  refreshBookings,
  showToast,
  setView,
  setSetupFromActivities,
  setCustomActivityType,
  setEditingKMSetup,
  setPendingSetupLoad,
  setActivitiesOpenId,
  courtReservation,
  setCourtReservation,
  reservationDraft,
  setReservationDraft,
}: UseActivityActionsDeps) {
  const rollingToppedRef = useRef(false);
  const [seriesEditPending, setSeriesEditPending] = useState(null);
  const [seriesEditConfirm, setSeriesEditConfirm] = useState(null); // 'future' | 'all' — extra confirmation

  const openActivitySetup = (activity) => {
    if (!activity) return;
    setSetupFromActivities(false);
    const t = activity.config?.tournamentType || 'americano';
    setCustomActivityType(['coaching', 'arrangement', 'custom'].includes(t) ? t : null);
    // KLUBBMESTERSKAP: open the KM wizard with the snapshot in publish mode (not the americano setup).
    if (t === 'klubbmesterskap') {
      const snap = activity.config?.kmSnapshot
        ? JSON.parse(JSON.stringify(activity.config.kmSnapshot))
        : null;
      if (snap) {
        snap.kmSignupMode = 'activity';
        snap.kmEditActivity = true;
        snap.fromActivityId = activity.id;
        if (activity.config?.pricePerPlayer != null)
          snap.kmActivityPrice = String(activity.config.pricePerPlayer);
        // Inject the registered pairs so they show up in the wizard (seeding etc.)
        const byBucket = {};
        (activity.registrations || [])
          .filter((r) => r.status === 'confirmed' && r.partnerId && r.bucketKey)
          .forEach((r) => {
            (byBucket[r.bucketKey] = byBucket[r.bucketKey] || []).push({
              id: `pair_${r.playerId}_${r.partnerId}`,
              p1Id: r.playerId,
              p2Id: r.partnerId,
            });
          });
        snap.registeredPairs = byBucket;
      }
      setEditingKMSetup({
        snapshot: snap,
        tournamentId: null,
        fromActivityId: activity.id,
        editActivity: true,
      });
      setView('klubbmesterskapSetup');
      return;
    }
    // PADEL24: open the Padel24 wizard with the snapshot in activity mode (not the americano setup).
    if (t === 'padel24') {
      setPendingSetupLoad({ activity });
      setView('wizard');
      return;
    }
    setPendingSetupLoad({ activity });
    const viewByType = {
      americano: 'americanoSetup',
      team_americano: 'teamAmericanoSetup',
      mexicano: 'mexicanoSetup',
      mixicano: 'mixicanoSetup',
      team_mexicano: 'teamMexicanoSetup',
      mix_americano: 'mixAmericanoSetup',
      club_americano: 'clubAmericanoSetup',
      club_team_americano: 'clubTeamAmericanoSetup',
      club_mexicano: 'clubMexicanoSetup',
    };
    setView(viewByType[t] || 'americanoSetup');
  };

  const topUpRollingSeries = async () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const dateOf = (a) => (a.bookingDraft?.slotRooms || [])[0]?.singleDate || null;
    const shift = (iso, unit, n) => {
      const [y, m, d] = iso.split('-').map(Number);
      const dt = new Date(y, m - 1, d);
      if (unit === 'month') dt.setMonth(dt.getMonth() + n);
      else dt.setDate(dt.getDate() + n * 7);
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    };
    const groups: any = {};
    (activities || []).forEach((a) => {
      if (a.recurringId && a.recurringRule?.mode === 'rolling')
        (groups[a.recurringId] = groups[a.recurringId] || []).push(a);
    });
    const newActs = [];
    const newBks = [];
    Object.values(groups).forEach((list: any) => {
      const withDate = list.filter(dateOf).sort((x, y) => dateOf(x).localeCompare(dateOf(y)));
      if (!withDate.length) return;
      const last = withDate[withDate.length - 1];
      const rule = last.recurringRule;
      const unit = rule.unit === 'month' ? 'month' : 'week';
      const interval = Math.max(1, rule.interval || 1);
      const cut = new Date(now);
      const hUnit = rule.horizonUnit === 'week' ? 'week' : 'month';
      const horizon = Math.max(1, rule.horizon || 1);
      if (hUnit === 'week') cut.setDate(cut.getDate() + horizon * 7);
      else cut.setMonth(cut.getMonth() + horizon);
      const cutoffISO = `${cut.getFullYear()}-${String(cut.getMonth() + 1).padStart(2, '0')}-${String(cut.getDate()).padStart(2, '0')}`;
      const have = new Set(withDate.map(dateOf));
      const isStandalone = last.bookingDraft?.slotMode === 'standalone';
      const baseBookings = isStandalone
        ? []
        : (bookings || []).filter((b) => b.seriesId === last.id);
      let cursor = dateOf(last);
      let idx = last.recurringIndex ?? withDate.length - 1;
      let guard = 0;
      while (guard++ < 60) {
        const nextISO = shift(cursor, unit, interval);
        cursor = nextISO;
        idx++;
        if (nextISO > cutoffISO) break;
        if (nextISO < todayISO || have.has(nextISO)) continue;
        const newId = `act_${Date.now()}_t${guard}_${Math.floor(Math.random() * 1000)}`;
        const newDraft = {
          ...last.bookingDraft,
          slotRooms: (last.bookingDraft?.slotRooms || []).map((r) => ({
            ...r,
            singleDate: nextISO,
          })),
        };
        newActs.push({
          ...last,
          id: newId,
          recurringIndex: idx,
          status: 'awaiting_signup',
          registrations: [],
          matches: [],
          createdAt: Date.now() + guard,
          bookingDraft: newDraft,
        });
        if (!isStandalone)
          for (const b of baseBookings)
            newBks.push({
              court: b.court,
              date: nextISO,
              startTime: b.startTime,
              endTime: b.endTime,
              title: b.title || last.config?.name || 'Aktivitet',
              seriesId: newId,
              source: 'recurring',
            });
        have.add(nextISO);
      }
    });
    if (newActs.length) {
      try {
        if (activityCommandsAreServerAuthoritative()) {
          // CREATE, not a whole-list rewrite. The recurring generator used to
          // send the club's entire activity list back with the new ones
          // appended, so anything created since the read was erased.
          await createActivities(newActs);
        } else {
          await persistActivities([...(activities || []), ...newActs]);
        }
        if (newBks.length) await addManyBookings(newBks, { adminOverride: false });
      } catch {
        /* ignore — best-effort top-up */
      }
    }
  };

  useEffect(() => {
    if (rollingToppedRef.current) return;
    if (!activities || !activities.length) return;
    rollingToppedRef.current = true;
    topUpRollingSeries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities]);

  const applySeriesEdit = async (scope) => {
    const pend = seriesEditPending;
    if (!pend) {
      return;
    }
    const { activity, config, bookingDraft } = pend;
    const base = (activities || []).find((a) => a.id === activity.id);
    const recId = base?.recurringId;
    const dateOf = (a) => (a.bookingDraft?.slotRooms || [])[0]?.singleDate || '';
    const baseDate = dateOf(base);
    const venueFields = bookingDraft
      ? {
          venueName: bookingDraft.venueName,
          venueAddress: bookingDraft.venueAddress,
          venuePostnr: bookingDraft.venuePostnr,
          venuePoststed: bookingDraft.venuePoststed,
        }
      : {};
    const inScope = (a) => {
      if (a.recurringId !== recId) return false;
      if (scope === 'all') return true;
      if (scope === 'future') return dateOf(a) >= baseDate;
      return a.id === activity.id;
    };
    let count = 0;
    const next = (activities || []).map((a) => {
      if (a.id === activity.id) {
        count++;
        return {
          ...a,
          config: { ...a.config, ...config },
          bookingDraft: bookingDraft || a.bookingDraft,
        };
      }
      if (inScope(a)) {
        count++;
        return {
          ...a,
          config: { ...a.config, ...config },
          bookingDraft: { ...a.bookingDraft, ...venueFields },
        };
      }
      return a;
    });
    try {
      if (activityCommandsAreServerAuthoritative()) {
        // THE SCOPE IS THE SERVER'S TO DERIVE. The request names the series and
        // the anchor; sending the ids `inScope` produced above would make
        // «future» whatever this tab believed a moment ago. `count` is still
        // used for the message, but the server's answer is what happened.
        const res = await saveWithRetry(() =>
          applyActivityScopeEdit({
            recurringId: recId,
            anchorId: activity.id,
            scope,
            config,
            ...(bookingDraft ? { bookingDraft } : {}),
          }),
        );
        count = res.updated;
        await refreshActivities();
      } else {
        await persistActivities(next);
      }
      showToast(
        'success',
        scope === 'this'
          ? 'Endringene lagret for denne aktiviteten.'
          : `Endringene lagret for ${count} aktiviteter i serien.`,
      );
    } catch (e) {
      showToast('error', e instanceof SaveRefused ? e.message : 'Kunne ikke lagre endringene.');
    }
    setSeriesEditPending(null);
    setActivitiesOpenId(activity.id);
    setView('activities');
  };

  const defaultActivityDraft = (act) => ({
    name: act?.config?.name || 'Aktivitet',
    slotMode: 'integrated',
    slotDuration: 90,
    bookingStart: act?.config?.activityDate || null,
    slotRooms: [
      {
        id: `room_${Date.now()}`,
        name: 'Tidsrom 1',
        recurring: false,
        singleDate: act?.config?.activityDate || '',
        weekdays: [],
        startTime: act?.config?.activityTime || '18:00',
        slotCount: Math.max(1, act?.config?.courts || 2),
        courtList: Array.from({ length: Math.max(1, act?.config?.courts || 2) }, (_, i) => ({
          id: `crt_${Date.now() + i}`,
          num: i + 1,
          name: '',
        })),
        classIds: [],
      },
    ],
  });

  const openActivityReservation = async (activityId) => {
    const act = activities.find((a) => a.id === activityId);
    if (!act) return;
    // Read fresh from Storage so we don't rely on a stale bookingsRef.
    let all: any;
    try {
      all = await fetchBookings();
    } catch {
      all = bookingsRef.current || bookings;
    }
    const actBookings = all.filter((b) => b.seriesId === activityId);
    let draft;
    if (actBookings.length) {
      // Build the time slot ENTIRELY from what is ACTUALLY booked — so the panel shows
      // reality (4 courts), not a stale wish (5 courts).
      const bookedCourts = [...new Set<number>(actBookings.map((b) => Number(b.court)))]
        .filter((n) => !isNaN(n))
        .sort((a, b) => a - b);
      const sample = actBookings
        .slice()
        .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''))[0];
      const base = act.bookingDraft || defaultActivityDraft(act);
      const room0 = (base.slotRooms && base.slotRooms[0]) || {};
      draft = {
        ...base,
        slotRooms: [
          {
            ...room0,
            id: room0.id || `room_${Date.now()}`,
            name: room0.name || 'Tidsrom 1',
            recurring: false,
            singleDate: sample.date || room0.singleDate || '',
            weekdays: [],
            startTime: sample.startTime || room0.startTime || '18:00',
            slotCount: bookedCourts.length,
            courtList: bookedCourts.map((num, i) => ({ id: `crt_${num}_${i}`, num, name: '' })),
            classIds: room0.classIds || [],
          },
        ],
      };
    } else {
      draft = act.bookingDraft || defaultActivityDraft(act);
    }
    setReservationDraft(draft);
    setCourtReservation(activityId);
  };

  const closeActivityReservation = () => {
    if (courtReservation && reservationDraft) {
      const stored = (activities || []).find((a) => a.id === courtReservation);
      if (activityCommandsAreServerAuthoritative()) {
        // ONE FIELD ON ONE ACTIVITY. This used to rewrite the club's whole list
        // to save a court draft, and it was not even awaited — a failure was
        // invisible. Now it names the activity, and says so when it fails.
        void saveWithRetry(() =>
          upsertActivity(courtReservation, { ...(stored || {}), bookingDraft: reservationDraft }),
        )
          .then((res) => adoptServerActivity(res.activity))
          .catch((e) =>
            showToast(
              'error',
              e instanceof SaveRefused ? e.message : 'Kunne ikke lagre banereservasjonen.',
            ),
          );
      } else {
        persistActivities(
          activities.map((a) =>
            a.id === courtReservation ? { ...a, bookingDraft: reservationDraft } : a,
          ),
        );
      }
    }
    setCourtReservation(null);
    setReservationDraft(null);
  };

  const handleReserveTournament = async ({ activityId, name, bookingDraft, tournamentType }) => {
    // Save the tournament immediately on reservation (status "awaiting setup"),
    // so it shows up in the calendar and is linkable before the setup is complete.
    const exists = activities.find((a) => a.id === activityId);
    const seedCourts = Math.max(
      0,
      ...(bookingDraft?.slotRooms || []).map((r) => (r.courtList || []).length),
      0,
    );
    const seedMax = Math.max(4, (seedCourts || 1) * 4);
    const rec = {
      ...(exists || {}),
      id: activityId,
      config: {
        allowReserves: true,
        maxReserves: 4,
        maxPlayers: seedMax,
        pricePerPlayer: 0,
        tournamentType: tournamentType || 'americano',
        ...(exists?.config || {}),
        ...(!bookingDraft?.noCourts && (bookingDraft?.slotRooms || [])[0]?.singleDate
          ? {
              activityDate: bookingDraft.slotRooms[0].singleDate,
              ...(bookingDraft.slotRooms[0].startTime
                ? { activityTime: bookingDraft.slotRooms[0].startTime }
                : {}),
              ...(bookingDraft.slotRooms[0].endTime
                ? { activityEndTime: bookingDraft.slotRooms[0].endTime }
                : {}),
            }
          : {}),
        name: name || 'Aktivitet',
      },
      bookingDraft: bookingDraft || exists?.bookingDraft || null,
      status: exists && exists.status !== 'awaiting_setup' ? exists.status : 'awaiting_setup',
      registrations: exists?.registrations || [],
      matches: exists?.matches || [],
      createdAt: exists?.createdAt || Date.now(),
    };
    try {
      if (activityCommandsAreServerAuthoritative()) {
        const res = await saveWithRetry(() => upsertActivity(rec.id, rec));
        adoptServerActivity(res.activity);
      } else {
        await persistActivities(
          exists ? activities.map((a) => (a.id === activityId ? rec : a)) : [...activities, rec],
        );
      }
    } catch (e) {
      showToast('error', e instanceof SaveRefused ? e.message : 'Kunne ikke lagre aktiviteten.');
      return;
    }
    showToast('success', `«${rec.config.name}» lagret — baner reservert.`);
  };

  const buildActivityOnCreate =
    (launch) =>
    async ({ config, players, activityMode, activityId, bookingDraft }) => {
      if (activityMode === 'activity') {
        // ACTIVITIES: players sign themselves up later — we do NOT build
        // the tournament here (it requires no players). Save/update the
        // activity with the setup + status "Awaiting signup". Court time is
        // already booked (tagged with activityId).
        const exists = activities.find((a) => a.id === activityId);
        const activity = {
          ...(exists || {}),
          id: activityId || `act_${Date.now()}`,
          config: { ...(exists?.config || {}), ...config },
          bookingDraft: bookingDraft || exists?.bookingDraft || null,
          status: 'awaiting_signup',
          registrations: exists?.registrations || [],
          matches: exists?.matches || [],
          createdAt: exists?.createdAt || Date.now(),
        };
        // RECURRING: for a NEW activity with recurrence → materialize N occurrences
        // (one real activity per session), linked by a shared recurringId. Calendar mode
        // clones the courts to each date (occupied ones are skipped via addManyBookings).
        const rec = bookingDraft?.recurrence;
        const extraActivities = [];
        const extraBookings = [];
        // RECURRING generation runs when recurrence is on AND the activity is not
        // already a series (recurringId is missing). This covers both brand-new
        // activities AND those that were pre-saved in the reserve step (awaiting_setup).
        if (rec?.enabled && !exists?.recurringId) {
          const recurringId = `rec_${Date.now()}`;
          const unit = rec.unit === 'month' ? 'month' : 'week';
          const interval = Math.max(1, Number(rec.interval) || 1);
          const mode = rec.mode === 'rolling' ? 'rolling' : 'count';
          const count = Math.max(1, Math.min(52, Number(rec.count) || 1));
          const horizon = Math.max(1, Math.min(12, Number(rec.horizon) || 1));
          const horizonUnit = rec.horizonUnit === 'week' ? 'week' : 'month';
          const rule = { unit, interval, mode, count, horizon, horizonUnit };
          const isStandalone = bookingDraft?.slotMode === 'standalone';
          const baseRoom = (bookingDraft?.slotRooms || [])[0] || {};
          const startISO = baseRoom.singleDate || bookingDraft?.bookingStart;
          const baseBookings = isStandalone
            ? []
            : (bookings || []).filter(
                (b) => b.seriesId === activity.id && b.source !== 'activity_linked',
              );
          const shiftDate = (iso, n) => {
            if (!iso) return iso;
            const [y, m, d] = iso.split('-').map(Number);
            const dt = new Date(y, m - 1, d);
            if (unit === 'month') dt.setMonth(dt.getMonth() + n);
            else dt.setDate(dt.getDate() + n * 7);
            return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
          };
          // How many occurrences are created NOW? Fixed number = count; rolling = up to the horizon.
          let occCount = count;
          if (mode === 'rolling') {
            const cut = new Date();
            cut.setHours(0, 0, 0, 0);
            if (horizonUnit === 'week') cut.setDate(cut.getDate() + horizon * 7);
            else cut.setMonth(cut.getMonth() + horizon);
            const cutoffISO = `${cut.getFullYear()}-${String(cut.getMonth() + 1).padStart(2, '0')}-${String(cut.getDate()).padStart(2, '0')}`;
            occCount = 1;
            let i = 1;
            while (i < 60) {
              const dISO = shiftDate(startISO, i * interval);
              if (!dISO || dISO > cutoffISO) break;
              occCount++;
              i++;
            }
          }
          activity.recurringId = recurringId;
          activity.recurringIndex = 0;
          activity.recurringCount = mode === 'rolling' ? null : occCount;
          activity.recurringRule = rule;
          // Series name (readable) + short unique code to identify the series.
          activity.seriesName = (config?.name || exists?.config?.name || 'Serie').trim();
          activity.seriesCode = recurringId.replace(/\D/g, '').slice(-4);
          for (let i = 1; i < occCount; i++) {
            const dISO = shiftDate(startISO, i * interval);
            const newId = `${activity.id}_r${i}`;
            const newDraft = {
              ...bookingDraft,
              recurrence: undefined,
              slotRooms: (bookingDraft?.slotRooms || []).map((r) => ({ ...r, singleDate: dISO })),
            };
            extraActivities.push({
              ...activity,
              id: newId,
              recurringId,
              recurringIndex: i,
              recurringRule: rule,
              config: { ...activity.config, ...(dISO ? { activityDate: dISO } : {}) },
              bookingDraft: newDraft,
              registrations: [],
              matches: [],
              createdAt: Date.now() + i,
            });
            // The courts for this date may already be reserved in the panel
            // (same deterministic ID). In that case we do NOT clone — avoids double-booking.
            const alreadyReserved = (bookings || []).some((b) => b.seriesId === newId);
            if (!isStandalone && dISO && !alreadyReserved) {
              for (const b of baseBookings)
                extraBookings.push({
                  court: b.court,
                  date: dISO,
                  startTime: b.startTime,
                  endTime: b.endTime,
                  title: b.title || config?.name || 'Aktivitet',
                  seriesId: newId,
                  source: 'recurring',
                });
            }
          }
        }
        // RECURRING EDITING: is the activity part of a series? Ask about the scope
        // (this / future / all) before we save.
        if (exists && exists.recurringId) {
          setSeriesEditPending({ activity, config, bookingDraft });
          return;
        }
        try {
          if (activityCommandsAreServerAuthoritative()) {
            // ONE BUSINESS ACTION: save this activity AND materialise its
            // further occurrences. The old list write made those atomic by
            // accident; two commands would be two chances to half-apply.
            const res = await saveWithRetry(() =>
              saveActivityWithOccurrences(activity, extraActivities),
            );
            adoptServerActivity(res.activity);
            adoptServerActivities(extraActivities);
          } else {
            const base = exists
              ? activities.map((a) => (a.id === activity.id ? activity : a))
              : [...activities, activity];
            await persistActivities([...base, ...extraActivities]);
          }
          let bookMsg = '';
          if (extraBookings.length) {
            const res = await addManyBookings(extraBookings, { adminOverride: false });
            if (res?.failed)
              bookMsg = ` (${res.failed} bane-tid${res.failed === 1 ? '' : 'er'} var opptatt og ble hoppet over)`;
          }
          showToast(
            'success',
            exists
              ? 'Oppsett lagret.'
              : extraActivities.length
                ? `${extraActivities.length + 1} aktiviteter opprettet.${bookMsg}`
                : 'Aktivitet opprettet — åpen for påmelding.',
          );
          setActivitiesOpenId(activity.id);
          setView('activities');
        } catch (e) {
          showToast(
            'error',
            e instanceof SaveRefused ? e.message : 'Kunne ikke lagre aktiviteten.',
          );
        }
        return;
      }
      launch({ config, players });
    };

  useEffect(() => {
    const checkAutoCancel = async () => {
      const now = Date.now();
      const cancelIds = [];
      for (const a of activities || []) {
        if (a.status != null && a.status !== 'awaiting_signup') continue;
        const minP = a.config?.minPlayers;
        if (minP == null) continue;
        const mine = (bookingsRef.current || bookings || []).filter((b) => b.seriesId === a.id);
        const stamps = mine
          .map((b) => new Date(`${b.date}T${b.startTime || '00:00'}:00`).getTime())
          .filter((n) => !isNaN(n));
        let startMs = stamps.length ? Math.min(...stamps) : Infinity;
        if (startMs === Infinity && a.bookingDraft?.slotMode === 'standalone') {
          const r = (a.bookingDraft.slotRooms || [])[0];
          if (r?.singleDate) {
            const ms = new Date(`${r.singleDate}T${r.startTime || '00:00'}:00`).getTime();
            if (!isNaN(ms)) startMs = ms;
          }
        }
        if (startMs === Infinity) continue;
        // Deadline: custom (N days/hours before start) if set, otherwise "when signup closes".
        const customMs =
          a.config?.minDeadlineBefore != null
            ? startMs -
              Math.max(0, Number(a.config.minDeadlineBefore) || 0) *
                (a.config.minDeadlineUnit === 'hours' ? 3600000 : 86400000)
            : null;
        const h =
          a.config?.signupCloseHours != null
            ? Math.max(0, Number(a.config.signupCloseHours) || 0)
            : 24;
        const deadlineMs = customMs != null && !isNaN(customMs) ? customMs : startMs - h * 3600000;
        if (now < deadlineMs) continue; // deadline not passed yet
        const confPlayers = (a.registrations || [])
          .filter((r) => r.status === 'confirmed')
          .reduce((s, r) => s + (r.partnerId ? 2 : 1), 0);
        if (confPlayers >= minP) continue; // minimum reached → it goes ahead
        cancelIds.push(a.id);
      }
      if (!cancelIds.length) return;
      const cancelSet = new Set(cancelIds);
      const bkIds = (bookingsRef.current || bookings || [])
        // NEVER delete a court the user booked themselves and merely LINKED to a
        // single-match activity — that booking is the user's own reservation, not
        // a court the activity created. Only auto-created activity courts go here.
        .filter((b) => cancelSet.has(b.seriesId) && b.source !== 'activity_linked')
        .map((b) => b.id);
      if (activityCommandsAreServerAuthoritative()) {
        // THE LIST ABOVE IS A PROPOSAL, NOT AUTHORITY. Cancelling refunds
        // people, so the server re-derives eligibility from its own data and
        // refuses the whole request if any named activity still qualifies — a
        // tab left open overnight must not be able to cancel a healthy one.
        //
        // AND IT RUNS FIRST, before the courts are released. The old order
        // freed the courts and then wrote the activities: a failure in between
        // left an activity standing with no courts and nothing reported.
        let res;
        try {
          res = await saveWithRetry(() => cancelActivitiesCommand(cancelIds, 'too_few'));
        } catch (e) {
          showToast(
            'error',
            e instanceof SaveRefused ? e.message : 'Kunne ikke avlyse aktivitetene.',
          );
          return;
        }
        if (res.refusals?.length) {
          // Not an error to the club: the server knows something this tab does
          // not — someone signed up, or the activity was already settled.
          await refreshActivities();
          return;
        }
        // THE COURTS WENT WITH IT, in the server's own transaction. Deleting
        // them from here was a second call that could fail on its own, leaving
        // a cancelled activity still holding its courts and nothing to say so.
        // The server derives which to release from the activities' `seriesId`,
        // applying the same `source !== 'activity_linked'` rule this code did.
        await refreshBookings();
        await refreshActivities();
      } else {
        if (bkIds.length) await deleteManyBookings(bkIds);
        await persistActivities(
          (activities || []).map((a) =>
            cancelSet.has(a.id)
              ? {
                  ...a,
                  status: 'cancelled',
                  cancelledReason: 'too_few',
                  cancelledAt: now,
                  signupClosed: true,
                  registrations: (a.registrations || []).map((r) =>
                    r.paidStatus === 'paid' ? { ...r, paidStatus: 'refunded', refundedAt: now } : r,
                  ),
                }
              : a,
          ),
        );
      }
      showToast(
        'info',
        `${cancelIds.length} aktivitet${cancelIds.length === 1 ? '' : 'er'} avlyst — for få påmeldte innen fristen. Baner frigjort, betalte refundert.`,
      );
    };
    checkAutoCancel();
    const id = setInterval(checkAutoCancel, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities, bookings]);

  return {
    openActivitySetup,
    openActivityReservation,
    closeActivityReservation,
    handleReserveTournament,
    buildActivityOnCreate,
    applySeriesEdit,
    seriesEditPending,
    setSeriesEditPending,
    seriesEditConfirm,
    setSeriesEditConfirm,
  };
}
